import { ArrowPathIcon, EyeIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import type { DataTableColumn } from '@odo/shared-ui';
import { PatientLink } from '../PatientLink';
import { RowMenu, type RowMenuItem } from '../RowMenu';
import { StatusBadge } from '../StatusBadge';
import { chargeNetCents, oneTimeDiscountAmount, oneTimeDiscountTooltip } from '../creditAllocation';
import type { ChargeListItem } from '../../../services/billing';
import { formatMoneyCents } from '../../../utils/money';

export type ChargesSortBy = 'practice' | 'patient' | 'total' | 'status' | 'date';

function shortRef(id: string): string {
    return id.slice(0, 8).toUpperCase();
}

function fmtDate(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}

function menuItemsFor(
    charge: ChargeListItem,
    canEdit: boolean,
    isRetrying: boolean,
    onOpen: (id: string) => void,
    onRetry: (id: string) => void,
    onCancel: (id: string) => void
): RowMenuItem[] {
    const items: RowMenuItem[] = [
        {
            label: 'View detail',
            icon: <EyeIcon className="h-4 w-4 text-gray-400" />,
            onClick: () => onOpen(charge.id)
        }
    ];
    if (!canEdit) return items;
    if (charge.status === 'error') {
        items.push({
            label: isRetrying ? 'Retrying…' : 'Retry',
            icon: <ArrowPathIcon className="h-4 w-4 text-gray-400" />,
            onClick: () => onRetry(charge.id),
            disabled: isRetrying
        });
    }
    if (charge.status === 'pending' || charge.status === 'error') {
        items.push({
            label: 'Cancel charge',
            icon: <NoSymbolIcon className="h-4 w-4" />,
            tone: 'danger',
            onClick: () => onCancel(charge.id)
        });
    }
    return items;
}

interface BuildChargeColumnsArgs {
    canEdit: boolean;
    isRetrying: boolean;
    onOpen: (id: string) => void;
    onRetry: (id: string) => void;
    onCancel: (id: string) => void;
}

export function buildChargeColumns({
    canEdit,
    isRetrying,
    onOpen,
    onRetry,
    onCancel
}: BuildChargeColumnsArgs): DataTableColumn<ChargeListItem, ChargesSortBy>[] {
    return [
        {
            key: 'ref',
            label: 'Charge',
            width: '9%',
            cellClassName: 'pl-6 pr-3 font-mono text-[13px] text-gray-500 whitespace-nowrap',
            headerClassName: 'whitespace-nowrap px-3 pl-6',
            render: (charge) => `#${shortRef(charge.id)}`
        },
        {
            key: 'practice',
            label: 'Practice',
            width: '16%',
            sortable: true,
            sortKey: 'practice',
            cellClassName: 'px-3',
            headerClassName: 'whitespace-nowrap px-3',
            render: (charge) => (
                <>
                    <span
                        className="block text-sm font-medium text-gray-800 truncate"
                        title={charge.practiceName}
                    >
                        {charge.practiceName}
                    </span>
                    {charge.practiceDoctorName && (
                        <span
                            className="block text-xs text-gray-400 truncate"
                            title={`Dr. ${charge.practiceDoctorName}`}
                        >
                            Dr. {charge.practiceDoctorName}
                        </span>
                    )}
                </>
            )
        },
        {
            key: 'patient',
            label: 'Patient',
            width: '14%',
            sortable: true,
            sortKey: 'patient',
            truncate: true,
            cellClassName: 'px-3 text-sm text-gray-700',
            headerClassName: 'whitespace-nowrap px-3',
            getTitle: (charge) => charge.patientName ?? undefined,
            render: (charge) => (
                <PatientLink patientId={charge.patientId} patientName={charge.patientName}>
                    {charge.patientName ?? <span className="text-gray-300">-</span>}
                </PatientLink>
            )
        },
        {
            key: 'product',
            label: 'Product',
            width: '20%',
            truncate: true,
            cellClassName: 'px-3 text-sm text-gray-700',
            headerClassName: 'whitespace-nowrap px-3',
            getTitle: (charge) => charge.productSummary ?? charge.description ?? undefined,
            render: (charge) =>
                charge.productSummary ??
                charge.description ?? <span className="text-gray-300">-</span>
        },
        {
            key: 'total',
            label: 'Total',
            width: '11%',
            sortable: true,
            sortKey: 'total',
            align: 'right',
            cellClassName: 'px-3 whitespace-nowrap',
            headerClassName: 'whitespace-nowrap px-3',
            getTitle: oneTimeDiscountTooltip,
            render: (charge) => {
                const discountAmount = oneTimeDiscountAmount(charge);
                return (
                    <>
                        <span className="text-sm font-semibold text-gray-900 tabular-nums">
                            {formatMoneyCents(chargeNetCents(charge))}
                        </span>
                        {discountAmount && (
                            <span className="block whitespace-normal text-[11px] leading-tight text-gray-400">
                                <span className="whitespace-nowrap">{discountAmount}</span> discount
                            </span>
                        )}
                    </>
                );
            }
        },
        {
            key: 'installments',
            label: 'Inst.',
            width: '6%',
            cellClassName: 'px-3 text-sm text-gray-500 whitespace-nowrap',
            headerClassName: 'whitespace-nowrap px-3',
            render: (charge) => `${charge.installments}×`
        },
        {
            key: 'status',
            label: 'Status',
            width: '10%',
            sortable: true,
            sortKey: 'status',
            cellClassName: 'px-3',
            headerClassName: 'whitespace-nowrap px-3',
            render: (charge) => <StatusBadge status={charge.status} />
        },
        {
            key: 'created',
            label: 'Created',
            width: '9%',
            sortable: true,
            sortKey: 'date',
            cellClassName: 'px-3 text-sm text-gray-500 whitespace-nowrap',
            headerClassName: 'whitespace-nowrap px-3',
            render: (charge) => fmtDate(charge.createdAt)
        },
        {
            key: 'menu',
            label: '',
            width: '5%',
            stopClickPropagation: true,
            cellClassName: 'px-3 w-12',
            headerClassName: 'whitespace-nowrap px-3',
            render: (charge) => {
                const items = menuItemsFor(charge, canEdit, isRetrying, onOpen, onRetry, onCancel);
                return items.length > 0 ? <RowMenu items={items} /> : null;
            }
        }
    ];
}
